import { getSupabase } from '../supabaseClient';
import type { StockEntity, StockCategoryEntity, StockPlaceEntity } from '../../types/data';
import { fetchAllPages } from './pagination';

const TABLE_STOCKS = 'stocks';
const TABLE_CATEGORIES = 'stock_categories';
const TABLE_PLACES = 'stock_places';

const stripAuditFields = <T extends { created_at?: unknown; modified_at?: unknown }>(input: T): Omit<T, 'created_at' | 'modified_at'> => {
  const { created_at, modified_at, ...rest } = input;
  return rest;
};

export const stockRepo = {
  async listStocks(): Promise<StockEntity[]> {
    const supabase = getSupabase();
    try {
      return await fetchAllPages<StockEntity>(() =>
        supabase
          .from(TABLE_STOCKS)
          .select('*')
          .eq('is_deleted', false)
          .order('stock_category_id', { ascending: true })
          .order('sort_order', { ascending: true })
          .order('created_at', { ascending: true })
          .order('id', { ascending: true })
      );
    } catch {
      // Backward-compatible fallback for environments where sort_order is not deployed yet.
    }

    return await fetchAllPages<StockEntity>(() =>
      supabase
        .from(TABLE_STOCKS)
        .select('*')
        .eq('is_deleted', false)
        .order('created_at', { ascending: true })
        .order('id', { ascending: true })
    );
  },

  async listCategories(): Promise<StockCategoryEntity[]> {
    const supabase = getSupabase();
    try {
      return await fetchAllPages<StockCategoryEntity>(() =>
        supabase
          .from(TABLE_CATEGORIES)
          .select('*')
          .eq('is_deleted', false)
          .order('sort_order', { ascending: true })
          .order('created_at', { ascending: true })
          .order('id', { ascending: true })
      );
    } catch {
      // Backward-compatible fallback for environments where sort_order is not deployed yet.
    }

    return await fetchAllPages<StockCategoryEntity>(() =>
      supabase
        .from(TABLE_CATEGORIES)
        .select('*')
        .eq('is_deleted', false)
        .order('created_at', { ascending: true })
        .order('id', { ascending: true })
    );
  },

  async listPlaces(): Promise<StockPlaceEntity[]> {
    const supabase = getSupabase();
    try {
      return await fetchAllPages<StockPlaceEntity>(() =>
        supabase
          .from(TABLE_PLACES)
          .select('*')
          .eq('is_deleted', false)
          .order('sort_order', { ascending: true })
          .order('created_at', { ascending: true })
          .order('id', { ascending: true })
      );
    } catch {
      // Backward-compatible fallback for environments where sort_order is not deployed yet.
    }

    return await fetchAllPages<StockPlaceEntity>(() =>
      supabase
        .from(TABLE_PLACES)
        .select('*')
        .eq('is_deleted', false)
        .order('created_at', { ascending: true })
        .order('id', { ascending: true })
    );
  },

  async createStock(input: Omit<StockEntity, 'id' | 'created_at' | 'modified_at' | 'is_deleted'> & { id?: string }): Promise<StockEntity> {
    const supabase = getSupabase();
    const row = {
      title: input.title,
      body: input.body,
      where_to_get: input.where_to_get,
      count: input.count ?? 0,
      is_deleted: false,
      stock_category_id: input.stock_category_id,
      stock_place_id: input.stock_place_id,
      sort_order: input.sort_order,
    };
    const { data, error } = await supabase
      .from(TABLE_STOCKS)
      .insert(row as any)
      .select()
      .single();
    if (error) throw error;
    return data as StockEntity;
  },

  async updateStock(id: string, patch: Partial<StockEntity>): Promise<StockEntity | null> {
    const supabase = getSupabase();
    const toUpdate = stripAuditFields(patch);
    if (Object.keys(toUpdate).length === 0) return null;
    const { data, error } = await supabase
      .from(TABLE_STOCKS)
      .update(toUpdate)
      .eq('id', id)
      .select()
      .single();
    if (error) throw error;
    return data as StockEntity;
  },

  async softDeleteStock(id: string): Promise<void> {
    const supabase = getSupabase();
    const { error } = await supabase
      .from(TABLE_STOCKS)
      .update({ is_deleted: true })
      .eq('id', id);
    if (error) throw error;
  },

  async createCategory(input: Omit<StockCategoryEntity, 'id' | 'created_at' | 'modified_at' | 'is_deleted'> & { id?: string }): Promise<StockCategoryEntity> {
    const supabase = getSupabase();
    const row = {
      title: input.title,
      body: input.body,
      is_deleted: false,
      sort_order: input.sort_order,
    };
    const { data, error } = await supabase
      .from(TABLE_CATEGORIES)
      .insert(row as any)
      .select()
      .single();
    if (error) throw error;
    return data as StockCategoryEntity;
  },

  async updateCategory(id: string, patch: Partial<StockCategoryEntity>): Promise<StockCategoryEntity | null> {
    const supabase = getSupabase();
    const toUpdate = stripAuditFields(patch);
    if (Object.keys(toUpdate).length === 0) return null;
    const { data, error } = await supabase
      .from(TABLE_CATEGORIES)
      .update(toUpdate)
      .eq('id', id)
      .select()
      .single();
    if (error) throw error;
    return data as StockCategoryEntity;
  },

  async createPlace(input: Omit<StockPlaceEntity, 'id' | 'created_at' | 'modified_at' | 'is_deleted'> & { id?: string }): Promise<StockPlaceEntity> {
    const supabase = getSupabase();
    const row = {
      title: input.title,
      body: input.body,
      is_deleted: false,
      sort_order: input.sort_order,
    };
    const { data, error } = await supabase
      .from(TABLE_PLACES)
      .insert(row as any)
      .select()
      .single();
    if (error) throw error;
    return data as StockPlaceEntity;
  },

  async updatePlace(id: string, patch: Partial<StockPlaceEntity>): Promise<StockPlaceEntity | null> {
    const supabase = getSupabase();
    const toUpdate = stripAuditFields(patch);
    if (Object.keys(toUpdate).length === 0) return null;
    const { data, error } = await supabase
      .from(TABLE_PLACES)
      .update(toUpdate)
      .eq('id', id)
      .select()
      .single();
    if (error) throw error;
    return data as StockPlaceEntity;
  },
};
